/**
 * Tipologie di offerta e loro abbinamento ai template.
 *
 * La tipologia è come Sinthema chiama l'offerta (Qualità, Sicurezza, Privacy…);
 * il template è il contenuto che finisce nel Word. Sono separati perché la
 * stessa tipologia può avere più modelli — tipicamente uno per l'assistenza e
 * uno per l'implementazione — e in fase di stesura l'abbinamento si sceglie.
 * Il template segnato come predefinito è quello proposto per primo.
 */
import { useEffect, useState } from 'react';
import { templateApi } from '../api';
import Modal from '../components/Modal';

const label = 'block text-xs font-medium text-gray-500 mb-0.5';
const vuoto = () => ({ nome: '', descrizione: '', ordine: 100, attivo: true, template: [] });

export default function TipologieOfferte() {
  const [rows, setRows] = useState([]);
  const [template, setTemplate] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(vuoto());
  const [saving, setSaving] = useState(false);

  const load = () => templateApi.tipologie().then(setRows).catch(console.error);
  useEffect(() => {
    load();
    templateApi.list({ attivi: 1 }).then(setTemplate).catch(() => setTemplate([]));
  }, []);

  const apriNuova = () => { setForm(vuoto()); setModal({ mode: 'new' }); };
  const apriModifica = (t) => {
    setForm({ ...t, template: (t.template || []).map(x => ({ ...x })) });
    setModal({ mode: 'edit', id: t.id });
  };

  const salva = async () => {
    if (!form.nome?.trim()) return alert('Il nome è obbligatorio');
    setSaving(true);
    try {
      const payload = {
        nome: form.nome, descrizione: form.descrizione,
        ordine: Number(form.ordine) || 100, attivo: form.attivo,
        template: form.template.map(x => ({ template_id: x.template_id, predefinito: !!x.predefinito })),
      };
      if (modal.mode === 'new') await templateApi.creaTipologia(payload);
      else await templateApi.aggiornaTipologia(modal.id, payload);
      setModal(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const elimina = async (t) => {
    if (!confirm(`Eliminare la tipologia "${t.nome}"?`)) return;
    try { await templateApi.eliminaTipologia(t.id); load(); }
    catch (e) { alert(e); }
  };

  const commutaAttivo = async (t) => {
    try { await templateApi.aggiornaTipologia(t.id, { attivo: !t.attivo }); load(); }
    catch (e) { alert(e); }
  };

  // Spuntare un template lo abbina; il pallino sceglie quale viene proposto per primo.
  const abbinato = (id) => form.template.some(x => String(x.template_id) === String(id));
  const commutaAbbinamento = (id) => setForm(p => {
    const c = abbinato(id)
      ? p.template.filter(x => String(x.template_id) !== String(id))
      : [...p.template, { template_id: id, predefinito: p.template.length === 0 }];
    // Tolto il predefinito, il primo rimasto prende il suo posto: una tipologia
    // senza predefinito non saprebbe cosa proporre in offerta.
    if (c.length && !c.some(x => x.predefinito)) c[0].predefinito = true;
    return { ...p, template: c };
  });
  const rendiPredefinito = (id) => setForm(p => ({
    ...p,
    template: p.template.map(x => ({ ...x, predefinito: String(x.template_id) === String(id) })),
  }));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-gray-900">Tipologie Offerte</h1>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Come chiami le tue offerte, e quali template propongono. Una tipologia può
            avere più template: quello predefinito è il primo proposto, gli altri restano
            scegliibili mentre scrivi l'offerta.
          </p>
        </div>
        <button className="btn-primary shrink-0" onClick={apriNuova}>+ Nuova tipologia</button>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-3">Tipologia</th>
              <th className="px-4 py-3">Template abbinati</th>
              <th className="px-4 py-3 text-center">Offerte</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400">Nessuna tipologia</td></tr>
            )}
            {rows.map(t => (
              <tr key={t.id} className={`border-b last:border-0 hover:bg-gray-50 ${t.attivo ? '' : 'opacity-50'}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{t.nome}</div>
                  {t.descrizione && <div className="text-xs text-gray-500">{t.descrizione}</div>}
                  {!t.attivo && <span className="text-[10px] font-semibold text-gray-500">DISATTIVATA</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {(t.template || []).length === 0
                      ? <span className="text-xs text-amber-600">nessun template abbinato</span>
                      : t.template.map(x => (
                          <span key={x.template_id}
                                className={`text-[11px] px-1.5 py-0.5 rounded border ${x.predefinito
                                  ? 'bg-brand-600 text-white border-brand-600'
                                  : 'bg-gray-50 text-gray-600 border-gray-300'}`}
                                title={x.predefinito ? 'Proposto per primo' : 'Disponibile in offerta'}>
                            {x.predefinito && '★ '}{x.nome}
                          </span>
                        ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-gray-500">{Number(t.offerte_collegate) || '—'}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button className="text-xs text-brand-600 hover:underline" onClick={() => apriModifica(t)}>modifica</button>
                  <button className="text-xs text-gray-500 hover:underline ml-3" onClick={() => commutaAttivo(t)}>
                    {t.attivo ? 'disattiva' : 'riattiva'}
                  </button>
                  <button className="text-xs text-gray-400 hover:text-red-600 ml-3" onClick={() => elimina(t)}>elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Nuova tipologia' : 'Modifica tipologia'}
               onClose={() => setModal(null)} size="lg">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-3">
                <label className={label}>Nome *</label>
                <input className="input text-sm" value={form.nome} placeholder="es. Sicurezza"
                       onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Ordine nei menu</label>
                <input className="input text-sm" type="number" value={form.ordine ?? 100}
                       onChange={e => setForm(p => ({ ...p, ordine: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className={label}>Descrizione</label>
              <input className="input text-sm" value={form.descrizione || ''}
                     onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} />
            </div>

            <div>
              <div className={label}>Template abbinati</div>
              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                {template.length === 0 && (
                  <div className="px-3 py-4 text-sm text-gray-400">
                    Nessun template attivo: creane uno da "Template Offerte".
                  </div>
                )}
                {template.map(tp => {
                  const on = abbinato(tp.id);
                  const pre = form.template.find(x => String(x.template_id) === String(tp.id))?.predefinito;
                  return (
                    <div key={tp.id} className={`flex items-center gap-3 px-3 py-2 ${on ? 'bg-brand-50/40' : ''}`}>
                      <input type="checkbox" className="w-4 h-4 cursor-pointer" checked={on}
                             onChange={() => commutaAbbinamento(tp.id)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-800 truncate">{tp.nome}</div>
                        <div className="text-xs text-gray-400">
                          {(tp.attivita || []).length} attività · {(tp.fasi || []).length} fasi
                          {tp.file_nome && ' · con file Word'}
                        </div>
                      </div>
                      {on && (
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer shrink-0">
                          <input type="radio" name="predefinito" checked={!!pre}
                                 onChange={() => rendiPredefinito(tp.id)} />
                          predefinito
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Il predefinito è quello proposto quando scegli questa tipologia in offerta.
                Gli altri restano selezionabili da lì.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={salva} disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva tipologia'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
